import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import LanguageToggle from '../components/LanguageToggle';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';
import EnrollmentFileModal from '../components/EnrollmentFileModal';
import SessionSettingsModal from '../components/SessionSettingsModal';
import { LOGO_ACCEPT, readLogoFile } from '../discovery/logo';
import { describeWorkingHours } from '../discovery/workingHours';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const statusClass = {
  active: 'bg-green-100 text-green-800',
  paused: 'bg-yellow-100 text-yellow-800',
  ended: 'bg-gray-100 text-gray-700',
  on: 'bg-green-100 text-green-800',
  stopped: 'bg-gray-100 text-gray-700',
};

const button =
  'inline-flex h-9 items-center justify-center rounded-md px-3 text-sm font-medium transition disabled:opacity-50';
const primaryButton = `${button} bg-primary-600 text-white hover:bg-primary-700`;
const secondaryButton = `${button} border border-gray-300 bg-white text-gray-800 hover:bg-gray-50`;
const quietButton = `${button} text-gray-600 hover:bg-gray-100`;
const dangerButton = `${button} border border-red-200 bg-white text-red-700 hover:bg-red-50`;

const DiscoverySession = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [payload, setPayload] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('document');
  const [pending, setPending] = useState('');
  const [answers, setAnswers] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [enrollmentOpen, setEnrollmentOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const loadGenerationRef = useRef(0);

  const load = useCallback(async () => {
    const generation = loadGenerationRef.current;
    const response = await axios.get(`${API_BASE_URL}/api/admin/discovery/sessions/${sessionId}`);
    if (generation !== loadGenerationRef.current) return;
    const data = response.data.data;
    setPayload(data);
    setLoadError('');
    setAnswers((current) => {
      const next = { ...current };
      for (const item of data.clarifications || []) {
        if (next[item.id] === undefined) next[item.id] = item.answer || '';
      }
      return next;
    });
  }, [sessionId]);

  useEffect(() => {
    let cancelled = false;
    loadGenerationRef.current += 1;
    load()
      .catch((error) => {
        console.error('Error fetching discovery session:', error);
        const message = error.response?.data?.error || 'Discovery master is unreachable';
        toast.error(message);
        const status = error.response?.status;
        if (!cancelled && status !== 400 && status !== 404) setLoadError(message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [load]);

  // Primitives, not the session object: a poll writes a new payload and must
  // not restart this interval.
  const pollingSessionId = payload?.session?.id || '';
  const summaryState = payload?.summaryState;

  useEffect(() => {
    if (!pollingSessionId) return undefined;
    const delay = summaryState === 'ready' ? 20000 : 4000;
    const timer = setInterval(() => {
      load().catch(() => {});
    }, delay);
    return () => clearInterval(timer);
  }, [load, pollingSessionId, summaryState]);

  const send = async (path, body, key) => {
    setPending(key);
    try {
      await axios.post(`${API_BASE_URL}/api/admin/discovery${path}`, body);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'The master could not save that change.');
    } finally {
      setPending('');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="mt-16">
          <LoadingSpinner />
        </div>
      </AdminLayout>
    );
  }

  if (loadError || !payload?.session) {
    return (
      <AdminLayout>
        <h1 className="text-2xl font-semibold text-gray-900">
          {loadError ? 'Could not load this session' : 'Session not found'}
        </h1>
        {loadError ? <p className="mt-2 max-w-xl text-sm text-gray-500">{loadError}</p> : null}
        <Link to="/discovery" className="mt-4 inline-block text-sm font-medium text-primary-700">
          All sessions
        </Link>
      </AdminLayout>
    );
  }

  const { session, agents, document: analysis, clarifications, checkedIn } = payload;
  const unansweredClarifications = (clarifications || []).filter((item) => !item.answer).length;

  const pickLogo = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      const dataUri = await readLogoFile(file);
      await send(`/sessions/${session.id}`, { logo: dataUri }, 'logo');
      toast.success('Logo saved');
    } catch (error) {
      toast.error(error.message || 'The logo could not be read.');
    }
  };
  // Computed by the master on every rebuild; also inside the document.
  const insights = payload.insights ?? analysis?.insights ?? [];
  const progress = payload.progress ?? analysis?.progress ?? null;

  return (
    <AdminLayout>
      <Link to="/discovery" className="text-sm font-medium text-primary-700 hover:text-primary-800">
        All sessions
      </Link>
      <div className="mt-3 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex items-center gap-4">
          {session.logoUrl ? (
            <img
              src={session.logoUrl}
              alt={`${session.companyName} logo`}
              className="h-14 w-auto max-w-[180px] shrink-0 rounded-lg border border-gray-200 bg-white object-contain p-1.5"
            />
          ) : null}
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">{session.companyName}</h1>
            <p className="mt-1 text-sm text-gray-500">
              Engagement through {session.endDate}. Agents report only into this session.
            </p>
            <p className="mt-1 text-sm text-gray-500">{describeWorkingHours(session.workingHours)}</p>
            <div className="mt-2 flex items-center gap-3 text-sm">
              <label htmlFor="session-logo" className="cursor-pointer font-medium text-primary-700 hover:text-primary-800">
                {session.logoUrl ? 'Change logo' : 'Add logo'}
              </label>
              <input id="session-logo" type="file" accept={LOGO_ACCEPT} className="sr-only" onChange={pickLogo} />
              {session.logoUrl ? (
                <button
                  type="button"
                  className="font-medium text-gray-500 hover:text-red-700 disabled:opacity-50"
                  disabled={pending !== ''}
                  onClick={() => send(`/sessions/${session.id}`, { logo: null }, 'logo')}
                >
                  Remove logo
                </button>
              ) : null}
              {pending === 'logo' ? <span className="text-gray-400">Saving…</span> : null}
            </div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {payload.cost ? (
            <div
              title={payload.cost.estimatedCostDetail}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-sm"
            >
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Est. cost</p>
              <p className="mt-0.5 text-xl font-bold leading-none tracking-tight text-gray-900">{payload.cost.estimatedCost}</p>
              <p className="mt-1 max-w-[18rem] text-[11px] leading-snug text-gray-500">{payload.cost.estimatedCostNote}</p>
            </div>
          ) : null}
          <span className={`inline-flex w-fit rounded-full px-2.5 py-0.5 text-xs font-medium ${statusClass[session.status]}`}>
            {session.status}
          </span>
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-3 rounded-xl border border-gray-200 bg-white px-3 py-3 shadow-sm lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          {session.status === 'ended' ? (
            <p className="px-1 text-sm text-gray-500">This engagement has ended. Agents are stopped.</p>
          ) : null}
          {session.status === 'active' ? (
            <button type="button" className={secondaryButton} disabled={pending !== ''} onClick={() => send(`/sessions/${session.id}`, { status: 'paused' }, 'pause')}>
              {pending === 'pause' ? 'Pausing…' : 'Pause all'}
            </button>
          ) : null}
          {session.status === 'paused' ? (
            <button type="button" className={primaryButton} disabled={pending !== ''} onClick={() => send(`/sessions/${session.id}`, { status: 'active' }, 'resume')}>
              {pending === 'resume' ? 'Resuming…' : 'Resume all'}
            </button>
          ) : null}
          {session.status !== 'ended' ? (
            <button type="button" className={dangerButton} disabled={pending !== ''} onClick={() => send(`/sessions/${session.id}`, { status: 'ended' }, 'end')}>
              {pending === 'end' ? 'Ending…' : 'End engagement'}
            </button>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-1">
          <button type="button" className={quietButton} disabled={pending !== ''} onClick={() => setSettingsOpen(true)}>
            Settings
          </button>
          <button type="button" className={quietButton} disabled={pending !== ''} onClick={() => setEnrollmentOpen(true)}>
            Enrollment file
          </button>
          <button
            type="button"
            className={quietButton}
            onClick={async () => {
              await navigator.clipboard.writeText(session.readUrl);
              toast.success('Read link copied');
            }}
          >
            Customer link
          </button>
          <button type="button" className={dangerButton} disabled={pending !== ''} onClick={() => setConfirmDelete(true)}>
            Delete
          </button>
        </div>
      </div>
      {settingsOpen ? (
        <SessionSettingsModal
          session={session}
          onClose={() => setSettingsOpen(false)}
          onSaved={async () => {
            await load();
          }}
        />
      ) : null}
      {enrollmentOpen ? (
        <EnrollmentFileModal
          sessionId={session.id}
          onClose={() => setEnrollmentOpen(false)}
          onCreated={async () => {
            await load();
          }}
        />
      ) : null}
      {confirmDelete ? (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-900">
            Delete this session? The enrollment file stops working, and the agents, cards, and customer link are removed.
          </p>
          <span className="flex gap-2">
            <button type="button" className={quietButton} disabled={pending !== ''} onClick={() => setConfirmDelete(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="inline-flex h-9 items-center rounded-md bg-red-700 px-3 text-sm font-medium text-white hover:bg-red-800 disabled:opacity-50"
              disabled={pending !== ''}
              onClick={async () => {
                setPending('delete');
                try {
                  await axios.delete(`${API_BASE_URL}/api/admin/discovery/sessions/${session.id}`);
                  toast.success('Session deleted');
                  navigate('/discovery');
                } catch (error) {
                  toast.error(error.response?.data?.error || 'The session could not be deleted.');
                  setPending('');
                }
              }}
            >
              {pending === 'delete' ? 'Deleting…' : 'Delete session'}
            </button>
          </span>
        </div>
      ) : null}

      {progress && (analysis?.evidenceCount || 0) > 0 ? (
        <ProgressStrip
          progress={progress}
          topInsight={insights[0] || null}
          hebrew={session.language === 'he'}
        />
      ) : null}

      <div className="mt-8 border-b border-gray-200">
        <nav className="-mb-px flex gap-6">
          {[
            ['document', 'Living document'],
            ['agents', 'Agents'],
            ['clarifications', unansweredClarifications > 0 ? `Clarifications (${unansweredClarifications})` : 'Clarifications'],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`border-b-2 pb-3 text-sm font-medium ${
                tab === key ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {label}
            </button>
          ))}
        </nav>
      </div>

      <div className="mt-6">
        {tab === 'document' ? (
          <DocumentView
            document={analysis}
            insights={insights}
            progress={progress}
            language={session.language === 'he' ? 'he' : 'en'}
            summaryState={payload.summaryState}
            pending={pending}
            onLanguage={(language) => send(`/sessions/${session.id}`, { language }, 'language')}
            onRewrite={async () => {
              setPending('summary');
              try {
                await axios.post(`${API_BASE_URL}/api/admin/discovery/sessions/${session.id}/summary`);
                await load();
              } catch (error) {
                toast.error(error.response?.data?.error || 'The summary could not be rewritten.');
              } finally {
                setPending('');
              }
            }}
          />
        ) : null}
        {tab === 'agents' ? (
          <AgentList
            sessionId={session.id}
            sessionStatus={session.status}
            agents={agents}
            checkedIn={checkedIn}
            pending={pending}
            onState={(agent, captureState) =>
              send(`/sessions/${session.id}/agents/${agent.id}`, { captureState }, agent.id)
            }
            onDelete={async (agent) => {
              setPending(`delete-${agent.id}`);
              try {
                await axios.delete(`${API_BASE_URL}/api/admin/discovery/sessions/${session.id}/agents/${agent.id}`);
                toast.success('Computer deleted');
                await load();
              } catch (error) {
                toast.error(error.response?.data?.error || 'The computer could not be deleted.');
              } finally {
                setPending('');
              }
            }}
          />
        ) : null}
        {tab === 'clarifications' ? (
          <ClarificationList
            clarifications={clarifications}
            answers={answers}
            setAnswers={setAnswers}
            pending={pending}
            onSave={(item) => send(`/sessions/${session.id}/clarifications/${item.id}`, { answer: answers[item.id] || '' }, item.id)}
          />
        ) : null}
      </div>
    </AdminLayout>
  );
};

const DOCUMENT_CHROME = {
  en: {
    company: 'Company',
    missingIntent: 'Add what the company does. The summary uses that as its lens.',
    processes: 'How each job runs',
    processesEmpty: 'No job has been seen yet.',
    typicalStretch: 'Typical visit',
    roles: 'Who does the work',
    rolesEmpty: 'No one has been seen at work yet.',
    systems: 'Tools',
    systemsEmpty: 'No tools have been seen yet.',
    apps: 'Apps in the business',
    flow: 'How work moves',
    flowEmpty: 'Work has not been seen moving between people yet.',
    betweenRoles: 'Between people',
    rework: 'Where the same work is done again',
    reworkEmpty: 'Nothing has been seen done twice yet.',
    friction: 'Done again',
    questions: 'Open questions',
    questionsEmpty: 'No open questions yet.',
    clarification: 'Answer.',
    waiting: 'Answer this on the Clarifications tab.',
    insights: 'What we noticed',
    insightsEmpty: 'Nothing to notice yet.',
    progress: 'How much we know',
    progressEstimate: 'A guess from the signals below.',
  },
  he: {
    company: 'החברה',
    missingIntent: 'הוסיפו מה החברה עושה. הסיכום משתמש בזה כעדשה.',
    processes: 'איך כל עבודה רצה',
    processesEmpty: 'עדיין לא ראינו עבודה.',
    typicalStretch: 'ביקור טיפוסי',
    roles: 'מי עושה את העבודה',
    rolesEmpty: 'עדיין לא ראינו מישהו בעבודה.',
    systems: 'כלים',
    systemsEmpty: 'עדיין לא ראינו כלים.',
    apps: 'הכלים בעסק',
    flow: 'איך העבודה עוברת',
    flowEmpty: 'עדיין לא ראינו עבודה שעוברת בין אנשים.',
    betweenRoles: 'בין אנשים',
    rework: 'איפה אותה עבודה נעשית שוב',
    reworkEmpty: 'עדיין לא ראינו עבודה שנעשית פעמיים.',
    friction: 'נעשה שוב',
    questions: 'שאלות פתוחות',
    questionsEmpty: 'עדיין אין שאלות פתוחות.',
    clarification: 'תשובה.',
    waiting: 'ענו על זה בלשונית ההבהרות.',
    insights: 'מה שמנו לב',
    insightsEmpty: 'עדיין אין מה לשים לב אליו.',
    progress: 'כמה אנחנו יודעים',
    progressEstimate: 'ניחוש מהאותות שלמטה.',
  },
};

function ProgressStrip({ progress, topInsight, hebrew }) {
  return (
    <div
      dir={hebrew ? 'rtl' : 'ltr'}
      className="mt-4 flex flex-col gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm sm:flex-row sm:items-center"
    >
      <div className="flex items-baseline gap-2 whitespace-nowrap">
        <span className="text-2xl font-semibold tabular-nums text-gray-900">{progress.percent}%</span>
        <span className="text-sm font-medium text-gray-700">{progress.stage}</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200" role="progressbar" aria-valuenow={progress.percent} aria-valuemin={0} aria-valuemax={100}>
        <div className="h-full rounded-full bg-primary-600" style={{ width: `${progress.percent}%` }} />
      </div>
      {topInsight ? (
        <p className="text-sm text-gray-500 sm:max-w-xs">
          <span className="font-medium text-gray-700">{mixDirection(topInsight.title)}</span>
          {topInsight.metric ? ` · ${topInsight.metric}` : ''}
        </p>
      ) : null}
    </div>
  );
}

function DocumentView({ document, insights = [], progress = null, language, summaryState, pending, onRewrite, onLanguage }) {
  if (!document) return null;
  const chrome = DOCUMENT_CHROME[language] || DOCUMENT_CHROME.en;
  const hebrew = language === 'he';
  const paragraphs = String(document.opening || '').split(/\n\n+/).map((paragraph) => paragraph.trim()).filter(Boolean);
  const stateLabel = {
    ready: 'Business summary',
    writing: 'Writing the business summary',
    stale: 'Business summary · new work since this was written',
    draft: 'Reading from the observed work',
  }[summaryState] || 'Reading from the observed work';
  return (
    <div className="w-full space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs font-medium uppercase tracking-[0.14em] text-gray-400">{stateLabel}</p>
        <div className="flex flex-wrap items-center gap-2">
          <LanguageToggle value={hebrew ? 'he' : 'en'} disabled={pending !== ''} onChange={onLanguage} />
          <button type="button" className={secondaryButton} disabled={pending !== ''} onClick={onRewrite}>
            {pending === 'summary' || summaryState === 'writing' ? 'Writing…' : 'Rewrite summary'}
          </button>
        </div>
      </div>
      <article dir={hebrew ? 'rtl' : 'ltr'} lang={hebrew ? 'he' : 'en'} className="space-y-8">
      <div className="space-y-4">
        {paragraphs.map((paragraph) => (
          <BidiParagraph key={paragraph.slice(0, 48)} text={paragraph} hebrewDocument={hebrew} className="text-lg leading-8 text-gray-900" />
        ))}
      </div>
      {document.intent ? (
        <aside className="rounded-xl border border-gray-200 bg-white px-5 py-4 shadow-sm">
          <p className="text-xs font-medium uppercase tracking-[0.14em] text-gray-400">{chrome.company}</p>
          <BidiParagraph text={document.intent} hebrewDocument={hebrew} className="mt-2 text-sm leading-6 text-gray-900" />
          {document.perspective ? <BidiParagraph text={document.perspective} hebrewDocument={hebrew} className="mt-2 text-sm leading-6 text-gray-500" /> : null}
        </aside>
      ) : (
        <p className="text-sm text-gray-500">{chrome.missingIntent}</p>
      )}
      {progress && (document.evidenceCount || 0) > 0 ? (
        <section>
          <h2 className="text-lg font-semibold text-gray-900">{chrome.progress}</h2>
          <div className="mt-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <span className="text-4xl font-semibold tabular-nums text-gray-900">{progress.percent}%</span>
              <span className="font-medium text-gray-800">{progress.stage}</span>
            </div>
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-gray-200" role="progressbar" aria-valuenow={progress.percent} aria-valuemin={0} aria-valuemax={100}>
              <div className="h-full rounded-full bg-primary-600" style={{ width: `${progress.percent}%` }} />
            </div>
            <BidiParagraph text={progress.summary} hebrewDocument={hebrew} className="mt-3 text-sm leading-6 text-gray-800" />
            <p className="mt-1 text-xs text-gray-500">{chrome.progressEstimate}</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              {(progress.signals || []).map((signal) => (
                <div key={signal.key} className="rounded-md border border-gray-200 px-3 py-2">
                  <p className="text-xs font-medium text-gray-900">{signal.label}</p>
                  <div className="mt-2 h-1 overflow-hidden rounded-full bg-gray-200">
                    <div className="h-full rounded-full bg-primary-600" style={{ width: `${Math.round((signal.share || 0) * 100)}%` }} />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">{signal.value}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}
      <WorkMap document={document} title={chrome.roles} empty={chrome.rolesEmpty} hebrew={hebrew} />
      <Section title={chrome.processes} empty={chrome.processesEmpty} items={document.processes || []} render={(process) => (
        <div key={`${process.role}-${process.name}`} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h3 className="text-base font-semibold text-gray-900">{process.name}</h3>
          <p className="mt-1 text-sm text-gray-500">
            {process.role}
            {process.frequency ? ` · ${process.frequency}` : ''}
            {process.typicalDuration ? ` ${chrome.typicalStretch}: ${process.typicalDuration}.` : ''}
          </p>
          {process.summary ? <BidiParagraph text={process.summary} hebrewDocument={hebrew} className="mt-3 text-sm leading-6 text-gray-800" /> : null}
          <Workflow steps={process.steps || []} />
        </div>
      )} />
      <section>
        <h2 className="text-lg font-semibold text-gray-900">{chrome.questions}</h2>
        {(document.gaps || []).length === 0 ? (
          <p className="mt-3 text-sm text-gray-500">{chrome.questionsEmpty}</p>
        ) : (
          <div className="mt-4 space-y-3">
            {(document.gaps || []).map((gap) => (
              <div key={gap.question} className="rounded-lg border border-gray-200 bg-white p-5">
                <BidiParagraph text={gap.question} hebrewDocument={hebrew} className="font-medium text-gray-900" />
                <BidiParagraph text={gap.reason} hebrewDocument={hebrew} className="mt-1 text-sm text-gray-500" />
                {gap.answer ? (
                  <p className="mt-3 text-sm text-gray-700">
                    <span className="font-medium">{chrome.clarification} </span>
                    {gap.answer}
                  </p>
                ) : (
                  <p className="mt-3 text-sm text-amber-800">{chrome.waiting}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
      <Section title={chrome.rework} empty={chrome.reworkEmpty} items={document.rework || []} render={(item) => (
        <Card key={item.description} title={chrome.friction} body={item.description} />
      )} />
      {(document.evidenceCount || 0) > 0 ? (
        <Section title={chrome.insights} empty={chrome.insightsEmpty} items={insights} render={(insight) => (
          <div key={`${insight.kind}-${insight.title}`} className="rounded-lg border border-gray-200 bg-white px-5 py-4">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h3 className="font-medium text-gray-900">{mixDirection(insight.title)}</h3>
              {insight.metric ? <span className="text-xs font-medium text-gray-500">{insight.metric}</span> : null}
            </div>
            <BidiParagraph text={insight.detail} hebrewDocument={hebrew} className="mt-1 text-sm leading-6 text-gray-600" />
          </div>
        )} />
      ) : null}
      <BusinessApps document={document} title={chrome.apps} hebrew={hebrew} />
      </article>
    </div>
  );
}

function BidiParagraph({ text, className, hebrewDocument = false }) {
  const value = String(text || '');
  const hasHebrew = /[\u0590-\u05FF]/.test(value);
  const align = hebrewDocument && !hasHebrew ? ' text-right' : '';
  return (
    <p dir={hasHebrew ? 'rtl' : 'ltr'} className={`${className}${align}`}>
      {mixDirection(value)}
    </p>
  );
}

// Wrap only the Latin words themselves in <bdi>. The old pattern also
// swallowed the trailing space and punctuation into the isolated run, and
// at a bidi boundary that space renders on the wrong side — Hebrew words
// looked glued to the English next to them.
const LATIN_RUN = /[A-Za-z][A-Za-z0-9]*(?:[ .,'’&/+#:-]+[A-Za-z0-9]+)*/g;

function mixDirection(text) {
  const value = String(text || '');
  if (!/[\u0590-\u05FF]/.test(value) || !/[A-Za-z]/.test(value)) return value;
  const parts = [];
  let last = 0;
  for (const match of value.matchAll(LATIN_RUN)) {
    if (match.index > last) parts.push(value.slice(last, match.index));
    parts.push(<bdi key={`${match.index}-${match[0].slice(0, 12)}`} dir="ltr">{match[0]}</bdi>);
    last = match.index + match[0].length;
  }
  if (last < value.length) parts.push(value.slice(last));
  return parts;
}

const BACKGROUND_APP =
  /^(explorer|msiexec|dwm|searchhost|applicationframehost|textinputhost|lockapp|systemsettings|shellexperiencehost|unidentified web app)$/i;

function leadApps(document) {
  const systems = document.systems || [];
  if (document.toolNames?.length) {
    return document.toolNames.map((name) => systems.find((system) => system.name === name) || { name });
  }
  return systems.filter((system) => system?.name && !BACKGROUND_APP.test(String(system.name).trim())).slice(0, 6);
}

function appsBlurb(document, hebrew) {
  if (document.toolsSummary) return document.toolsSummary;
  const shown = leadApps(document);
  if (shown.length === 0) return '';
  const notable = (document.systems || []).filter(
    (system) => system?.name && !BACKGROUND_APP.test(String(system.name).trim()),
  );
  const extra = Math.max(0, notable.length - shown.length);
  const names = shown.map((system) => system.name);
  if (hebrew) {
    const where = names.join(', ');
    return extra > 0
      ? `העסק עובד ב-${where}. יישומים אחרים נפתחים מדי פעם, והם לא חלק משגרת היום.`
      : `העסק עובד ב-${where}.`;
  }
  const where =
    names.length <= 1
      ? names[0] || ''
      : names.length === 2
        ? `${names[0]} and ${names[1]}`
        : `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
  return extra > 0
    ? `People here work in ${where}. Other applications open from time to time, and they are not part of the day-to-day.`
    : `People here work in ${where}.`;
}

function BusinessApps({ document, title, hebrew }) {
  const summary = appsBlurb(document, hebrew);
  const apps = leadApps(document);
  if (!summary && apps.length === 0) return null;
  return (
    <section>
      <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
      {summary ? (
        <BidiParagraph text={summary} hebrewDocument={hebrew} className="mt-3 max-w-3xl text-sm leading-6 text-gray-700" />
      ) : null}
      {apps.length > 0 ? (
        <ul className="mt-3 flex flex-wrap gap-2">
          {apps.map((system) => (
            <li
              key={system.name}
              className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs text-gray-700"
            >
              {system.icon ? (
                <img src={system.icon} alt="" className="h-3.5 w-3.5 shrink-0 rounded-sm object-contain" draggable={false} />
              ) : null}
              {mixDirection(system.name)}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function FlowArrow({ reverse = false }) {
  return (
    <span aria-hidden className="inline-block shrink-0 self-center text-gray-400 rtl:-scale-x-100">
      {reverse ? '←' : '→'}
    </span>
  );
}

// An arrow is only drawn between two neighboring roles (either direction).
// Handoffs that skip a role, run the other way once that slot is taken, or
// have no ends still need their description, and `what` never fit on the arrow.
function flowLabel(item, between) {
  const ends = [item.from, item.to].filter(Boolean).join(' → ');
  if (item.via && ends) return `${ends} · ${item.via}`;
  return item.via || ends || between;
}

function neighborLink(flows, placed, fromRole, toRole) {
  if (!fromRole || !toRole) return -1;
  return flows.findIndex((item, index) => !placed.has(index) && item.from === fromRole && item.to === toRole);
}

function WorkMap({ document, title, empty, hebrew }) {
  const labels = DOCUMENT_CHROME[hebrew ? 'he' : 'en'];
  const roles = document.roles || [];
  const flows = document.informationFlow || [];
  const placed = new Set();
  const links = roles.map((role, index) => {
    const next = roles[index + 1];
    if (!next) return null;
    let flowIndex = neighborLink(flows, placed, role.role, next.role);
    let reverse = false;
    if (flowIndex === -1) {
      flowIndex = neighborLink(flows, placed, next.role, role.role);
      reverse = flowIndex !== -1;
    }
    if (flowIndex === -1) return null;
    placed.add(flowIndex);
    return { flow: flows[flowIndex], reverse };
  });
  const listed = flows.filter((item, index) => !placed.has(index) || item.what);
  return (
    <section>
      <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
      {roles.length === 0 ? (
        <p className="mt-3 text-sm text-gray-500">{empty}</p>
      ) : (
        <div className="mt-4 flex flex-wrap items-stretch gap-3">
          {roles.map((role, index) => {
            const link = links[index];
            return (
              <div key={role.role} className="flex min-w-0 items-stretch gap-3">
                <div className="w-64 max-w-full rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <h3 className="font-medium text-gray-900">{mixDirection(role.role)}</h3>
                  <BidiParagraph text={role.summary} hebrewDocument={hebrew} className="mt-1 text-sm leading-6 text-gray-600" />
                </div>
                {link ? (
                  <div className="flex w-16 shrink-0 flex-col items-center justify-center gap-1 text-center">
                    <FlowArrow reverse={link.reverse} />
                    {link.flow.via ? <span className="text-[11px] leading-4 text-gray-500">{mixDirection(link.flow.via)}</span> : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
      {listed.length > 0 ? (
        <div className="mt-4 space-y-3">
          <h3 className="text-sm font-semibold text-gray-900">{labels.flow}</h3>
          {listed.map((item, index) => (
            <Card
              key={`${item.from || ''}-${item.to || ''}-${item.via || ''}-${index}`}
              title={flowLabel(item, labels.betweenRoles)}
              body={item.what}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}

function Workflow({ steps }) {
  if (!steps.length) return null;
  return (
    <ol className="mt-4 flex gap-2 overflow-x-auto border-t border-gray-200 pt-4">
      {steps.map((step, index) => (
        <li key={`${step.order}-${step.control}-${step.action}`} className="flex items-stretch gap-2">
          <div className="w-44 shrink-0 rounded-md border border-gray-200 px-3 py-2">
            <p className="text-xs text-gray-500">{mixDirection(step.control)}</p>
            <p className="mt-1 text-sm leading-5 text-gray-900">{mixDirection(step.action)}</p>
          </div>
          {index < steps.length - 1 ? <FlowArrow /> : null}
        </li>
      ))}
    </ol>
  );
}

function Section({ title, empty, items, render }) {
  return (
    <section>
      <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
      {items.length === 0 ? <p className="mt-3 text-sm text-gray-500">{empty}</p> : <div className="mt-4 space-y-3">{items.map(render)}</div>}
    </section>
  );
}

function Card({ title, body }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-5 py-4">
      <h3 className="font-medium text-gray-900">{mixDirection(title)}</h3>
      <BidiParagraph text={body} hebrewDocument={/[\u0590-\u05FF]/.test(`${title} ${body}`)} className="mt-1 text-sm leading-6 text-gray-600" />
    </div>
  );
}

function AgentList({ sessionId, sessionStatus, agents, checkedIn, pending, onState, onDelete }) {
  const [confirmingId, setConfirmingId] = useState('');
  if (agents.length === 0) {
    return (
      <Empty
        title="No computers have checked in"
        body="Install the observer with this session's enrollment file. The machine appears here on its first check-in."
      />
    );
  }
  return (
    <ul className="space-y-3">
      {agents.map((agent) => (
        <li key={agent.id} className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to={`/discovery/${sessionId}/agents/${agent.id}`}
                  className="font-medium text-primary-700 hover:text-primary-800"
                >
                  {agent.machineName}
                </Link>
                <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${statusClass[agent.captureState] || statusClass.ended}`}>
                  {agent.captureState}
                </span>
              </div>
              <p className="mt-1 text-sm text-gray-500">
                {agent.role} · {agent.os} · {agent.cardsSent} cards · last check-in {checkedIn[agent.id]} Israel time
                {agent.workingHours ? ` · ${describeWorkingHours(agent.workingHours)}` : ''}
                {agent.costLimitReached
                  ? ` · stopped at its ${agent.costLimit} cost limit`
                  : agent.costLimit
                    ? ` · cost limit ${agent.costLimit}`
                    : ''}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {sessionStatus === 'ended' ? <p className="text-sm text-gray-500">Stopped with the engagement.</p> : null}
              {sessionStatus === 'paused' ? <p className="text-sm text-gray-500">Paused with the engagement.</p> : null}
              {sessionStatus === 'active' && agent.captureState === 'on' ? (
                <button type="button" className={secondaryButton} disabled={pending !== ''} onClick={() => onState(agent, 'paused')}>Pause</button>
              ) : null}
              {sessionStatus === 'active' && agent.captureState !== 'on' ? (
                <button type="button" className={primaryButton} disabled={pending !== ''} onClick={() => onState(agent, 'on')}>Resume</button>
              ) : null}
              {sessionStatus !== 'ended' && agent.captureState !== 'stopped' ? (
                <button type="button" className={dangerButton} disabled={pending !== ''} onClick={() => onState(agent, 'stopped')}>Stop</button>
              ) : null}
              <button type="button" className={dangerButton} disabled={pending !== ''} onClick={() => setConfirmingId(agent.id)}>Delete</button>
            </div>
          </div>
          {confirmingId === agent.id ? (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
              <p className="max-w-xl text-sm text-red-900">
                Delete {agent.machineName}? Its {agent.cardsSent} cards, frames, and event log are removed, and the
                living document is rebuilt without them. A computer still running the observer will check in again
                and reappear.
              </p>
              <span className="flex gap-2">
                <button type="button" className={quietButton} disabled={pending !== ''} onClick={() => setConfirmingId('')}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="inline-flex h-9 items-center rounded-md bg-red-700 px-3 text-sm font-medium text-white hover:bg-red-800 disabled:opacity-50"
                  disabled={pending !== ''}
                  onClick={async () => {
                    await onDelete(agent);
                    setConfirmingId('');
                  }}
                >
                  {pending === `delete-${agent.id}` ? 'Deleting…' : 'Delete computer'}
                </button>
              </span>
            </div>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function ClarificationList({ clarifications, answers, setAnswers, pending, onSave }) {
  if (clarifications.length === 0) {
    return (
      <Empty
        title="No gaps are open"
        body="The master files a question when observed work leaves a path unfinished. Answers are written into the document as clarifications."
      />
    );
  }
  return (
    <ul className="space-y-4">
      {clarifications.map((item) => (
        <li key={item.id} className="rounded-lg border border-gray-200 bg-white p-5">
          <h2 className="font-medium text-gray-900">{item.question}</h2>
          <p className="mt-1 text-sm text-gray-500">{item.reason}</p>
          <form
            className="mt-4 space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              onSave(item);
            }}
          >
            <label className="block text-sm font-medium text-gray-700" htmlFor={`clarification-${item.id}`}>
              FDE clarification
            </label>
            <textarea
              id={`clarification-${item.id}`}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              rows={3}
              value={answers[item.id] || ''}
              onChange={(event) => setAnswers((current) => ({ ...current, [item.id]: event.target.value }))}
              placeholder="Describe what the observed work did not show."
              required
            />
            <button
              type="submit"
              disabled={pending !== '' || (answers[item.id] || '').trim().length < 3}
              className="inline-flex items-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            >
              Save clarification
            </button>
          </form>
        </li>
      ))}
    </ul>
  );
}

function Empty({ title, body }) {
  return (
    <div className="rounded-lg border border-dashed border-gray-300 bg-white px-5 py-8">
      <h2 className="text-lg font-medium text-gray-900">{title}</h2>
      <p className="mt-2 max-w-xl text-sm text-gray-500">{body}</p>
    </div>
  );
}

export default DiscoverySession;
